import React from 'react';
import {
  Modal,
  View,
  StyleSheet,
  Platform,
  Pressable,
  TouchableOpacity,
  useWindowDimensions,
  ViewStyle,
  TextStyle,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Typography } from './ui';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n';
import { fonts } from '../styles';

import { ACCENT_TEXT, ON_ACCENT_TEXT } from '../styles/accentText';
import { surfaceFill } from '../styles/platformBackgrounds';
type Mode = 'guest' | 'joined';

interface Props {
  visible: boolean;
  mode: Mode;
  onClose: () => void;
  onLogIn?: () => void;
  onSignUp?: () => void;
  onInvite?: () => void;
}

export const PracticeJoinModal: React.FC<Props> = ({
  visible,
  mode,
  onClose,
  onLogIn,
  onSignUp,
  onInvite,
}) => {
  const { isDark, colors: themeColors } = useTheme();
  const { i18n } = useI18n();
  const { width } = useWindowDimensions();
  const ru = i18n.language === 'ru';
  const isDesktop = width >= 768;

  const isGuest = mode === 'guest';

  const heading = isGuest
    ? (ru ? 'Ты на верном пути' : 'You\'re on the right path')
    : (ru ? 'Ждём тебя на практике' : 'See you at the practice');

  const body = isGuest
    ? (ru
        ? 'Чтобы сохранить Практику и получить напоминание за 30 минут до начала — войди в свой кабинет. Если ты с нами впервые, создай аккаунт — это займёт меньше минуты.'
        : 'To save the Practice and get a 30-minute reminder, sign in to your account. New here? Create one — takes less than a minute.')
    : (ru
        ? 'Событие уже в твоём календаре. Agapella — наш AI-проводник — напомнит за 30 минут до начала. А пока позови тех, с кем хотелось бы пройти эту практику вместе.'
        : 'The event is now in your calendar. Agapella — our AI guide — will remind you 30 minutes before. For now, invite those you\'d love to share this practice with.');

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable
          testID="agape-practice-success-sheet"
          accessible={false}
          onPress={(e) => e.stopPropagation()}
          style={[
            styles.card,
            isDesktop && styles.cardDesktop,
            // CORE-1733 (возврат QA) — окно — поверхность v4.
            surfaceFill(isDark ? 'dark' : 'light'),
          ]}
        >
          <View style={[styles.iconCircle, { backgroundColor: isGuest ? 'rgba(233, 130, 69, 0.10)' : '#E98245' }]}>
            <Ionicons
              name={isGuest ? 'log-in-outline' : 'checkmark'}
              size={26}
              color={isGuest ? ACCENT_TEXT : ON_ACCENT_TEXT}
            />
          </View>

          <Typography style={[styles.heading, { color: themeColors.textPrimary }]}>
            {heading}
          </Typography>

          <Typography style={[styles.body, { color: themeColors.textSecondary }]}>
            {body}
          </Typography>

          {isGuest ? (
            <View style={styles.buttonsColumn}>
              <TouchableOpacity
                activeOpacity={0.9}
                onPress={onLogIn}
                style={styles.btnPrimary}
              >
                <Typography style={styles.btnPrimaryText}>
                  {ru ? 'Войти' : 'Sign in'}
                </Typography>
              </TouchableOpacity>
              <TouchableOpacity
                activeOpacity={0.9}
                onPress={onSignUp}
                style={[
                  styles.btnSecondary,
                  { borderColor: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.12)' },
                ]}
              >
                <Typography style={[styles.btnSecondaryText, { color: themeColors.textPrimary }]}>
                  {ru ? 'Создать аккаунт' : 'Create account'}
                </Typography>
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity
              activeOpacity={0.9}
              onPress={onInvite}
              style={styles.btnPrimary}
            >
              <Ionicons name="paper-plane-outline" size={16} color={ON_ACCENT_TEXT} style={{ marginRight: 10 }} />
              <Typography style={styles.btnPrimaryText}>
                {ru ? 'Пригласить друзей' : 'Invite friends'}
              </Typography>
            </TouchableOpacity>
          )}

          <Pressable testID="agape-practice-later" onPress={onClose} style={styles.skip}>
            <Typography style={[styles.skipText, { color: themeColors.textMuted }]}>
              {ru ? 'Позже' : 'Maybe later'}
            </Typography>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(4px)',
    } as any),
  } as any,
  card: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 28,
    paddingHorizontal: 28,
    paddingTop: 36,
    paddingBottom: 24,
    alignItems: 'center',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 24px 80px rgba(0,0,0,0.20), 0 0 0 1px rgba(0,0,0,0.04)',
    } as any),
  } as any,
  cardDesktop: {
    paddingHorizontal: 40,
    paddingTop: 44,
    paddingBottom: 32,
  } as ViewStyle,
  iconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  } as ViewStyle,
  heading: {
    fontSize: 22,
    lineHeight: 28,
    fontWeight: '700',
    fontFamily: fonts.display,
    letterSpacing: -0.3,
    textAlign: 'center',
    marginBottom: 12,
  } as TextStyle,
  body: {
    fontSize: 15,
    lineHeight: 22,
    fontWeight: '400',
    fontFamily: fonts.text,
    textAlign: 'center',
    marginBottom: 28,
    opacity: 0.82,
  } as TextStyle,
  buttonsColumn: {
    width: '100%',
    gap: 10,
  } as ViewStyle,
  btnPrimary: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#E98245',
    paddingVertical: 15,
    borderRadius: 100,
    ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
  } as any,
  btnPrimaryText: {
    fontSize: 14,
    fontWeight: '700',
    fontFamily: fonts.display,
    color: ON_ACCENT_TEXT,
    letterSpacing: 1,
    textTransform: 'uppercase',
    lineHeight: 18,
  } as TextStyle,
  btnSecondary: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 15,
    borderRadius: 100,
    borderWidth: 1.5,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
  } as any,
  btnSecondaryText: {
    fontSize: 14,
    fontWeight: '600',
    fontFamily: fonts.display,
    letterSpacing: 0.3,
    lineHeight: 18,
  } as TextStyle,
  skip: {
    marginTop: 16,
    paddingVertical: 8,
    paddingHorizontal: 16,
    ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
  } as any,
  skipText: {
    fontSize: 14,
    fontWeight: '500',
    fontFamily: fonts.text,
    letterSpacing: 0.2,
  } as TextStyle,
});
